import { BuilderElement, GlobalConfig } from "@/types/builder";
import { generateId } from "@/utils/builder";
import { createFooterMenuSection, createFooterEndSection } from "./template2";

// ====================== Header Section ======================
const createHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundColor: "background",
    padding: "14px 0",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "20px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1496px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0 50px",
      },
      children: [
        {
          id: generateId(),
          type: "logo" as const,
          styles: {
            maxWidth: "136px",
            display: "flex",
            alignItems: "center",
          },
          responsiveStyles: {
            mobile: {
              maxWidth: "90px",
            },
          },
          children: [
            {
              id: generateId(),
              type: "image" as const,
              src: "/images/movyo/logo.webp",
              alt: "Movyo Logo",
              styles: {
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Disclaimer Header Section ======================
const createDisclaimerHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "93px 0",
    backgroundColor: "#F7F7F7",
    backdropFilter: "blur(14.3px)",
    color: "forground",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "60px 0",
    },
    mobile: {
      padding: "40px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1382px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
      },
      responsiveStyles: {
        mobile: {
          padding: "0 20px",
        },
        tablet: {
          padding: "0 20px",
        },
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          },
          responsiveStyles: {
            tablet: {
              flexDirection: "column",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            },
            mobile: {
              flexDirection: "column",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Disclaimer",
              styles: {
                textAlign: "left",
                fontFamily: "Manrope, sans-serif",
                fontSize: "70px",
                fontStyle: "normal",
                fontWeight: "800",
                lineHeight: "80px",
                letterSpacing: "-2.1px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "60px",
                  lineHeight: "60px",
                },
                tablet: {
                  fontSize: "50px",
                  lineHeight: "50px",
                },
                mobile: {
                  fontSize: "45px",
                  lineHeight: "45px",
                  fontWeight: "700",
                },
              },
            },
            {
              id: generateId(),
              type: "heading" as const,
              content: "Date Effective: April 13, 2025",
              styles: {
                textAlign: "center",
                fontFamily: "Manrope, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "600",
                lineHeight: "80px",
                letterSpacing: "-1.2px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                tablet: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  textAlign: "left",
                  fontSize: "25px",
                  lineHeight: "25px",
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Disclaimer Content Section ======================
const createDisclaimerContentSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "120px 0 104px",
    backgroundColor: "background",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "80px 0",
    },
    mobile: {
      padding: "60px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1339px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
        display: "flex",
        flexDirection: "column",
        gap: "40px",
      },
      responsiveStyles: {
        mobile: {
          padding: "0 20px",
        },
        tablet: {
          padding: "0 20px",
        },
      },
      children: [
        // Disclaimer Introduction
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Disclaimer",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "55px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.55px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "45px",
                  lineHeight: "45px",
                },
                mobile: {
                  fontSize: "40px",
                  lineHeight: "40px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content:
                "The information provided on Movyo.net, including all content, materials, products, and services, is intended for general informational purposes only and is not intended to substitute professional medical advice, diagnosis, or treatment. By using this website, you acknowledge and agree to the terms of this disclaimer in full.",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: No Medical Advice
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "No Medical Advice",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "20px 0",
              },
              responsiveStyles: {
                desktop: {
                  gap: "18px 0",
                },
                mobile: {
                  gap: "15px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "Movyo.net is not a medical organization, and we do not provide medical advice, diagnosis, or treatment. The content on this website, including product descriptions, testimonials, blog posts, or other materials, is for informational purposes only and should not be interpreted as medical advice. Always consult your physician or another qualified health provider before starting any new supplement, especially if you are pregnant, nursing, taking prescription medications, or have any medical conditions.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "You should not disregard, delay, or avoid seeking medical advice because of information you have read on this website or in materials associated with our products.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
          ],
        },
        // Section: FDA Disclaimer
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "FDA Disclaimer",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content:
                "Statements made on this website regarding dietary supplements have not been evaluated by the Food and Drug Administration (FDA). Our products are not intended to diagnose, treat, cure, or prevent any disease. The results mentioned on this site or in any marketing materials are not guaranteed and may vary from person to person.",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: Individual Results May Vary
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Individual Results May Vary",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "20px 0",
              },
              responsiveStyles: {
                desktop: {
                  gap: "18px 0",
                },
                mobile: {
                  gap: "15px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "Any testimonials or customer reviews found on Movyo.net are individual experiences shared by users of our products. They are not intended to represent or guarantee that any other customer will achieve the same or similar results. Results depend on many factors, including individual body chemistry, lifestyle, diet, and health conditions.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "We do not claim that our products will produce specific outcomes, and we do not make guarantees about weight loss, performance, energy, or any other health-related goals.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
          ],
        },
        // Section: Paid Endorsements and Actors
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Paid Endorsements and Actors",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content:
                "In some cases, testimonials, interviews, or product reviews on this website may be written by paid endorsers, and some videos or images may include the use of actors to protect customer privacy. These representations are intended to illustrate potential benefits and are not intended to mislead or imply average results.",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: Affiliate Relationships
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Affiliate Relationships",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content:
                "Movyo.net may receive compensation from third-party websites, advertisers, or affiliates for promoting their products or services. This includes commission-based relationships for traffic or purchases made through affiliate links. Any such compensation does not influence the content, claims, or integrity of our materials.",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
        // Section: Use of the Website
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Use of the Website",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "20px 0",
              },
              responsiveStyles: {
                desktop: {
                  gap: "18px 0",
                },
                mobile: {
                  gap: "15px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "Your use of this website and any purchases made from it are subject to our full Terms and Conditions, Privacy Policy, and Refund Policy. We reserve the right to update or modify this Disclaimer at any time without prior notice. Changes will be effective immediately upon posting to this page.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "If you do not agree to all the terms set forth in this Disclaimer, you must not use this website or its products.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
          ],
        },
        // Section: Contact
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "30px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "22px 0",
            },
            mobile: {
              gap: "18px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Contact",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "-0.4px",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  fontSize: "30px",
                  lineHeight: "30px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as const,
              content:
                "If you have questions about this Disclaimer or anything related to our products or policies, please reach out to us at support@movyo.net. We're happy to clarify any concerns you may have.",
              styles: {
                color: "forground",
                fontFamily: "Poppins, sans-serif",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "40px",
                letterSpacing: "-0.28px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "24px",
                  lineHeight: "36px",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "24px",
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

export const template2Disclaimer = (globalConfig: GlobalConfig) => {
  return {
    header: createHeaderSection(globalConfig) as BuilderElement,
    disclaimerHeader: createDisclaimerHeaderSection(
      globalConfig
    ) as BuilderElement,
    disclaimerContent: createDisclaimerContentSection(
      globalConfig
    ) as BuilderElement,
    footerMenu: createFooterMenuSection(globalConfig) as BuilderElement,
    footerEnd: createFooterEndSection(globalConfig) as BuilderElement,
  };
};

export const template2DisclaimerGlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "Poppins, sans-serif",
      fontSize: "48px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "Poppins, sans-serif",
      fontSize: "36px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-3",
      fontFamily: "Poppins, sans-serif",
      fontSize: "24px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-4",
      fontFamily: "Poppins, sans-serif",
      fontSize: "20px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-5",
      fontFamily: "Poppins, sans-serif",
      fontSize: "18px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-6",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#FFFFFF",
    forground: "#333333",
    primary: "#667eea",
    secondary: "#764ba2",
    tertiary: "#f8f9fa",
    quaternary: "#e0e0e0",
    link: "#667eea",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};
